import ms from 'ms'

import { SchedulerDay } from '@/components/integration/nylas/scheduler/hooks/useSchedulerDays'
import {
  AvailabilityFilter,
  ClientHomeAddress,
  EVERY_ADVISER_AVAILABILITY_FILTER,
  InPersonMeetingFormat,
  SchedulerAvailabilitySource,
  SchedulerOwningEntityType,
} from '@/components/integration/nylas/scheduler/types'
import { MEETING_FORMAT } from '@/lib/constants'
import { QUERY_KEYS } from '@/lib/queryKeys'
import availabilityService from '@/lib/services/availabilityService'
import { MeetingFormat } from '@/types/misc'
import { BookableMeetingFormat } from '@/types/requests/meetings'

/**
 * Stable `YYYY-MM-DD` key for the calendar day that `date` falls on
 * within the given IANA `timezone`. Use when comparing scheduler
 * timeslots (whose `Date` values are absolute instants) against a
 * displayed local day.
 */
export function getDayKeyInTimezone(date: Date, timezone: string): string {
  const dayParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)

  const year = dayParts.find((part) => part.type === 'year')?.value
  const month = dayParts.find((part) => part.type === 'month')?.value
  const day = dayParts.find((part) => part.type === 'day')?.value

  return `${year}-${month}-${day}`
}

/**
 * Stable `YYYY-MM-DD` key derived from the browser-local components
 * of `date`. Use for the calendar grid, where each cell represents a
 * local calendar day with no timezone interpretation.
 */
export function getLocalDayKey(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

/**
 * Anchors a calendar day to UTC noon, giving a stable Date identifier
 * for "this calendar day" that survives the timezone-offset edge cases
 * around midnight. Used as the canonical representation for the user's
 * selected date inside the scheduler.
 */
export function getCalendarAnchorDate(date: Date): Date {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12),
  )
}

/**
 * Formats a Date as a short 12-hour time string in the given timezone,
 * e.g. `9:30 AM`. Uses the browser's default locale.
 */
export function formatTimeShort(date: Date, timezone: string): string {
  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone,
  })
}

/**
 * Returns how many timeslots are available for the day identified by
 * `inputDateKey`. The key must already be in `YYYY-MM-DD` form - the
 * caller chooses whether to derive it via `getLocalDayKey` (for the
 * full-calendar grid) or `getDayKeyInTimezone` (for timezone-aware
 * comparisons). Returns `0` while loading or if there are no matches.
 */
export function getSlotCountForDate(
  inputDateKey: string,
  schedulerDays: SchedulerDay[] | undefined,
  timezone: string,
  isLoading: boolean,
): number {
  if (isLoading || !schedulerDays) {
    return 0
  }

  const matchingSchedulerDay = schedulerDays.find(
    (schedulerDay) =>
      getDayKeyInTimezone(schedulerDay.date, timezone) === inputDateKey,
  )

  return matchingSchedulerDay?.timeSlotStartTimes.length ?? 0
}

export function buildAvailabilityQueryOptions(
  type: SchedulerOwningEntityType,
  owningEntityId: number | string,
  start: Date,
  end: Date,
  availabilityFilter: AvailabilityFilter = EVERY_ADVISER_AVAILABILITY_FILTER,
  availabilitySource?: SchedulerAvailabilitySource,
) {
  return {
    staleTime: ms('5m'),
    queryKey: QUERY_KEYS.availability(
      type,
      owningEntityId,
      start.toISOString(),
      end.toISOString(),
      availabilityFilter.locationId,
      availabilityFilter.inPersonFormat,
      availabilitySource?.key,
    ),
    queryFn: async () => {
      const availability = availabilitySource
        ? await availabilitySource.fetchAvailability(
            start,
            end,
            availabilityFilter,
          )
        : await availabilityService.getAvailability(
            type,
            owningEntityId,
            start,
            end,
            availabilityFilter,
          )

      return {
        timeSlots: availability.timeSlots.map((timeSlot) => ({
          start: new Date(timeSlot.startTime * 1000),
          end: new Date(timeSlot.endTime * 1000),
        })),
      }
    },
  }
}

/**
 * Whether a meeting in this format can be booked. Every format can be now, but
 * the guard is kept so a format added to the enum later is rejected until the
 * booking flow handles it.
 */
export function isBookableMeetingFormat(
  meetingFormat: MeetingFormat,
): meetingFormat is BookableMeetingFormat {
  return (
    meetingFormat === MEETING_FORMAT.video ||
    meetingFormat === MEETING_FORMAT.phone ||
    meetingFormat === MEETING_FORMAT.in_person_office ||
    meetingFormat === MEETING_FORMAT.in_person_client_home
  )
}

export function isInPersonMeetingFormat(
  meetingFormat: MeetingFormat | null,
): meetingFormat is InPersonMeetingFormat {
  return (
    meetingFormat === MEETING_FORMAT.in_person_office ||
    meetingFormat === MEETING_FORMAT.in_person_client_home
  )
}

export function isSameAvailabilityFilter(
  filterA: AvailabilityFilter,
  filterB: AvailabilityFilter,
): boolean {
  return (
    filterA.locationId === filterB.locationId &&
    filterA.inPersonFormat === filterB.inPersonFormat
  )
}

/**
 * Whether every part of the client's home address has been given, so a
 * meeting there can be booked.
 */
export function isClientHomeAddressComplete(
  clientHomeAddress: ClientHomeAddress,
): boolean {
  return (
    clientHomeAddress.addressLine1.trim() !== '' &&
    clientHomeAddress.townOrCity.trim() !== '' &&
    clientHomeAddress.postcode.trim() !== ''
  )
}

/**
 * The client's home address on one line, e.g. "14 Eaton Road, Norwich, NR4 7TJ",
 * leaving out any empty parts.
 */
export function formatClientHomeAddress(clientHomeAddress: {
  addressLine1: string | null
  townOrCity: string | null
  postcode: string | null
}): string {
  return [
    clientHomeAddress.addressLine1,
    clientHomeAddress.townOrCity,
    clientHomeAddress.postcode,
  ]
    .map((addressPart) => addressPart?.trim())
    .filter((addressPart) => !!addressPart)
    .join(', ')
}
